<template>
  <div class="product-detail-page">
    <div class="gallery-section">
      <button class="floating-back" type="button" aria-label="返回上一页" @click="$router.back()">‹</button>

      <div
        v-if="galleryMedias.length > 0"
        class="gallery-stage"
        @touchstart.passive="onGalleryTouchStart"
        @touchend.passive="onGalleryTouchEnd"
      >
        <img
          :src="galleryMedias[currentMediaIndex].mediaUrl"
          class="gallery-main-image"
          :alt="`${product.title || '产品图片'} ${currentMediaIndex + 1}`"
        />
        <div v-if="galleryMedias.length > 1" class="gallery-count">
          {{ currentMediaIndex + 1 }}/{{ galleryMedias.length }}
        </div>
      </div>
      <div v-else class="gallery-placeholder">暂无产品图片</div>

      <div v-if="galleryMedias.length > 1" ref="thumbnailStrip" class="thumbnail-strip">
        <button
          v-for="(media, index) in galleryMedias"
          :key="media.id || index"
          type="button"
          class="thumbnail-item"
          :class="{ active: currentMediaIndex === index }"
          :aria-label="`查看第 ${index + 1} 张产品图片`"
          @click="selectMedia(index)"
        >
          <img :src="media.mediaUrl" :alt="`${product.title || '产品缩略图'} ${index + 1}`" />
        </button>
      </div>
    </div>

    <main v-if="!loading && !loadError" class="detail-content">
      <section class="product-summary-card">
        <h1 class="product-title">{{ product.title }}</h1>
        <div v-if="product.subTitle" class="product-subtitle">{{ product.subTitle }}</div>

        <div class="price-number-row">
          <div v-if="displayMinPrice !== null" class="product-price">
            <span class="currency">¥</span><strong>{{ formatPrice(displayMinPrice) }}</strong><span class="price-suffix">起</span>
          </div>
          <div v-else class="product-price product-price--empty">敬请期待</div>
          <div class="product-number">编号：{{ product.id }}</div>
        </div>

        <div v-if="productKeywords.length > 0" class="keyword-list">
          <span v-for="keyword in productKeywords" :key="keyword" class="keyword-tag">{{ keyword }}</span>
        </div>

        <div class="purchase-hint">
          本产品展示价格即最终购买价格，您可直接下单购买，我们将尽快与您联系，如有任何疑问可直接联系客服。
        </div>
      </section>

      <section v-if="skuItems.length > 0" class="sku-section" aria-label="产品规格">
        <article v-for="item in skuItems" :key="item.sku.id" class="sku-card">
          <div class="sku-heading-row">
            <div class="sku-heading-copy">
              <h2 class="sku-title">{{ item.sku.title }}</h2>
              <div v-if="item.sku.subTitle" class="sku-subtitle">{{ item.sku.subTitle }}</div>
            </div>
            <div v-if="item.sku.minPrice !== null && item.sku.minPrice !== undefined" class="sku-price-pill">
              <span>¥</span><strong>{{ formatPrice(item.sku.minPrice) }}</strong><small>起</small>
            </div>
            <div v-else class="sku-price-pill sku-price-pill--empty">敬请期待</div>
          </div>

          <div v-if="skuTags(item.sku).length > 0" class="sku-tag-action-row">
            <div class="sku-tag-list">
              <span
                v-for="tag in skuTags(item.sku)"
                :key="`${item.sku.id}-${tag.type}-${tag.label}`"
                class="sku-tag"
                :class="{ 'sku-tag--type': tag.type === 'type' }"
              >{{ tag.label }}</span>
            </div>
            <button
              v-if="item.sku.useCalendarPrice"
              type="button"
              class="reserve-btn"
              @click="selectSku(item.sku)"
            >预定</button>
          </div>
          <div v-else-if="item.sku.useCalendarPrice" class="sku-action-only">
            <button type="button" class="reserve-btn" @click="selectSku(item.sku)">预定</button>
          </div>

          <div v-if="!item.sku.useCalendarPrice" class="spec-list">
            <div
              v-for="spec in item.specs"
              :key="spec.id"
              class="spec-row"
              :class="{ 'spec-row--sold-out': spec.stock <= 0, 'spec-row--selected': selectedSpec && selectedSpec.id === spec.id }"
              @click="selectSpec(item.sku, spec)"
            >
              <div class="spec-info">
                <div class="spec-tag-list">
                  <span v-for="tag in splitTags(spec.tags)" :key="`${spec.id}-${tag}`" class="spec-tag">{{ tag }}</span>
                  <span v-if="splitTags(spec.tags).length === 0" class="spec-tag">默认规格</span>
                </div>
                <div class="spec-meta">
                  <strong>¥{{ formatPrice(spec.price) }}</strong>
                  <span :class="{ 'stock-out': spec.stock <= 0 }">{{ spec.stock > 0 ? `库存 ${spec.stock}` : '无货' }}</span>
                </div>
              </div>
            </div>
            <div v-if="item.specs.length === 0" class="spec-empty">暂无可售规格</div>
            <div v-if="selectedSpec && selectedSpecSkuId === item.sku.id" class="spec-purchase-bar">
              <div class="spec-quantity">
                <span class="spec-purchase-label">购买数量</span>
                <button type="button" @click.stop="changeSpecQuantity(-1)">−</button>
                <strong>{{ specQuantity }}</strong>
                <button type="button" @click.stop="changeSpecQuantity(1)">＋</button>
              </div>
              <button type="button" class="spec-buy-btn" @click.stop="goSpecOrder">立即购买</button>
            </div>
          </div>

          <div v-if="item.richTexts.length > 0" class="sku-rich-texts">
            <div v-for="richText in item.richTexts" :key="richText.id" class="sku-rich-text-item">
              <h3 v-if="richText.title">{{ richText.title }}</h3>
              <div class="rich-text-body" v-html="richText.content"></div>
            </div>
          </div>
        </article>
      </section>

      <section v-if="richTexts.length > 0" class="product-content-section">
        <nav v-if="richTexts.length > 1" class="content-tabs" aria-label="产品详情目录">
          <button
            v-for="(richText, index) in richTexts"
            :key="richText.id"
            type="button"
            :class="{ active: activeContentIndex === index }"
            @click="scrollToContent(index)"
          >{{ richText.title || `详情${index + 1}` }}</button>
        </nav>

        <article
          v-for="(richText, index) in richTexts"
          :key="richText.id"
          :ref="`contentSection${index}`"
          class="content-card"
        >
          <h2 v-if="richText.title" class="content-title">{{ richText.title }}</h2>
          <div class="rich-text-body" v-html="richText.content"></div>
        </article>
      </section>
    </main>

    <div v-if="loading" class="page-state">产品加载中...</div>
    <div v-else-if="loadError" class="page-state page-state--error">
      <div>{{ loadError }}</div>
      <button type="button" @click="loadProductDetail($route.params.id)">重新加载</button>
    </div>
  </div>
</template>

<script>
import { getProductDetail } from '@/api/browse'

function splitText(value) {
  if (Array.isArray(value)) return value.map(item => String(item).trim()).filter(Boolean)
  if (typeof value !== 'string') return []
  return value.split(/[，,；;、。.]/).map(item => item.trim()).filter(Boolean)
}

export default {
  name: 'ProductDetail',
  data() {
    return {
      product: {},
      medias: [],
      skuItems: [],
      richTexts: [],
      currentMediaIndex: 0,
      galleryTouchStartX: null,
      galleryTimer: null,
      activeContentIndex: 0,
      selectedSpec: null,
      selectedSpecSkuId: null,
      specQuantity: 1,
      loading: true,
      loadError: ''
    }
  },
  computed: {
    galleryMedias() {
      return this.medias.filter(media => Number(media.mediaType) !== 2 && media.mediaUrl)
    },
    productKeywords() {
      return splitText(this.product.keywords)
    },
    displayMinPrice() {
      if (this.product.minPrice !== null && this.product.minPrice !== undefined) return this.product.minPrice
      const prices = this.skuItems
        .map(item => Number(item.sku.minPrice))
        .filter(price => Number.isFinite(price) && price >= 0)
      return prices.length > 0 ? Math.min(...prices) : null
    }
  },
  mounted() {
    this.loadProductDetail(this.$route.params.id)
  },
  beforeDestroy() {
    this.stopGalleryAutoplay()
  },
  methods: {
    async loadProductDetail(id) {
      if (!id) return
      this.stopGalleryAutoplay()
      this.loading = true
      this.loadError = ''
      try {
        const res = await getProductDetail(id)
        const data = res.data || {}
        this.product = data.product || {}
        this.medias = Array.isArray(data.medias) ? data.medias : []
        this.richTexts = Array.isArray(data.richTexts) ? data.richTexts : []
        this.skuItems = (Array.isArray(data.skus) ? data.skus : []).map(item => ({
          sku: item.sku || item,
          specs: Array.isArray(item.specs) ? item.specs : [],
          richTexts: Array.isArray(item.richTexts) ? item.richTexts : []
        }))
        // 固定规格商品默认选中页面上第一个有库存的规格，用户打开详情即可调整数量并购买。
        const firstFixedSku = this.skuItems.find(item => !item.sku.useCalendarPrice && item.specs.some(spec => Number(spec.stock) > 0))
        const firstAvailableSpec = firstFixedSku && firstFixedSku.specs.find(spec => Number(spec.stock) > 0)
        this.selectedSpec = firstAvailableSpec || null
        this.selectedSpecSkuId = firstFixedSku ? firstFixedSku.sku.id : null
        this.specQuantity = 1
        this.currentMediaIndex = 0
        this.startGalleryAutoplay()
      } catch (error) {
        console.error('加载产品详情失败:', error)
        this.loadError = error.message || '加载失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    formatPrice(value) {
      const price = Number(value)
      if (!Number.isFinite(price)) return '--'
      return Number.isInteger(price) ? String(price) : price.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
    },
    splitTags(value) {
      return splitText(value)
    },
    skuTags(sku) {
      const tags = []
      splitText(sku.skuType).forEach(label => tags.push({ type: 'type', label }))
      splitText(sku.keyWord).forEach(label => tags.push({ type: 'keyword', label }))
      return tags
    },
    selectMedia(index, restartAutoplay = true) {
      if (index < 0 || index >= this.galleryMedias.length) return
      this.currentMediaIndex = index
      if (restartAutoplay) this.startGalleryAutoplay()
      this.$nextTick(() => {
        const strip = this.$refs.thumbnailStrip
        const target = strip && strip.children ? strip.children[index] : null
        if (!strip || !target) return
        const left = Math.max(0, target.offsetLeft - (strip.clientWidth - target.clientWidth) / 2)
        if (typeof strip.scrollTo === 'function') {
          strip.scrollTo({ left, behavior: 'smooth' })
        } else {
          strip.scrollLeft = left
        }
      })
    },
    onGalleryTouchStart(event) {
      this.stopGalleryAutoplay()
      const touch = event.changedTouches && event.changedTouches[0]
      this.galleryTouchStartX = touch ? touch.clientX : null
    },
    onGalleryTouchEnd(event) {
      if (this.galleryTouchStartX === null || this.galleryMedias.length < 2) return
      const touch = event.changedTouches && event.changedTouches[0]
      if (!touch) {
        this.startGalleryAutoplay()
        return
      }
      const distance = touch.clientX - this.galleryTouchStartX
      this.galleryTouchStartX = null
      if (Math.abs(distance) < 40) {
        this.startGalleryAutoplay()
        return
      }
      const direction = distance < 0 ? 1 : -1
      const nextIndex = (this.currentMediaIndex + direction + this.galleryMedias.length) % this.galleryMedias.length
      this.selectMedia(nextIndex)
    },
    startGalleryAutoplay() {
      this.stopGalleryAutoplay()
      if (this.galleryMedias.length < 2) return
      this.galleryTimer = window.setInterval(() => {
        const nextIndex = (this.currentMediaIndex + 1) % this.galleryMedias.length
        this.selectMedia(nextIndex, false)
      }, 3000)
    },
    stopGalleryAutoplay() {
      if (this.galleryTimer !== null) {
        window.clearInterval(this.galleryTimer)
        this.galleryTimer = null
      }
    },
    selectSku(sku) {
      if (sku && !sku.useCalendarPrice) {
        const firstAvailable = this.skuItems.find(item => Number(item.sku.id) === Number(sku.id))?.specs?.find(spec => Number(spec.stock) > 0)
        if (firstAvailable) this.selectSpec(sku, firstAvailable)
        return
      }
      this.$router.push({
        name: 'ProductCalendar',
        params: { id: this.product.id },
        query: { skuId: sku.id }
      })
    },
    selectSpec(sku, spec) {
      if (!sku || !spec || Number(spec.stock) <= 0) return
      this.selectedSpec = spec
      this.selectedSpecSkuId = sku.id
      this.specQuantity = 1
    },
    changeSpecQuantity(delta) {
      if (!this.selectedSpec) return
      const stock = Math.max(1, Number(this.selectedSpec.stock) || 1)
      this.specQuantity = Math.min(stock, Math.max(1, this.specQuantity + delta))
    },
    goSpecOrder() {
      if (!this.selectedSpec || !this.selectedSpecSkuId) return
      const sku = this.skuItems.find(item => Number(item.sku.id) === Number(this.selectedSpecSkuId))?.sku || {}
      this.$router.push({
        name: 'OrderCreate',
        params: { id: this.product.id },
        query: {
          mode: 'spec',
          productId: this.product.id,
          skuId: this.selectedSpecSkuId,
          skuName: sku.title || '',
          specId: this.selectedSpec.id,
          specTags: this.selectedSpec.tags || '',
          unitPrice: this.selectedSpec.price,
          quantity: this.specQuantity,
          productName: this.product.title || '',
          requiredInfo: sku.requiredInfo || '',
          isMailingMode: this.product.isMailingMode ? '1' : '0'
        }
      })
    },
    scrollToContent(index) {
      this.activeContentIndex = index
      const target = this.$refs[`contentSection${index}`]
      const element = Array.isArray(target) ? target[0] : target
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}
</script>

<style lang="scss" scoped>
.product-detail-page {
  width: 100%;
  max-width: 750px;
  min-height: 100vh;
  margin: 0 auto;
  padding-bottom: max(24px, env(safe-area-inset-bottom));
  background: #f4faf5;
  color: #222;
}

.gallery-section { position: relative; background: #111; }

.floating-back {
  position: absolute;
  top: max(14px, env(safe-area-inset-top));
  left: 14px;
  z-index: 5;
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: rgba(0, 0, 0, 0.28);
  font-size: 31px;
  line-height: 34px;
  text-align: center;
  cursor: pointer;
  backdrop-filter: blur(5px);
}

.gallery-stage {
  position: relative;
  width: 100%;
  aspect-ratio: 1.42 / 1;
  overflow: hidden;
  background: #ececec;
  touch-action: pan-y;
}

.gallery-main-image { display: block; width: 100%; height: 100%; object-fit: cover; }

.gallery-count {
  position: absolute;
  right: 14px;
  bottom: 14px;
  padding: 4px 9px;
  border-radius: 12px;
  color: #fff;
  background: rgba(0, 0, 0, 0.48);
  font-size: 12px;
}

.gallery-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1.42 / 1;
  color: #8b8b8b;
  background: #ececec;
}

.thumbnail-strip {
  display: flex;
  gap: 9px;
  padding: 10px 8px 13px;
  overflow-x: auto;
  background: linear-gradient(180deg, #282828 0%, #080808 100%);
  scrollbar-width: none;

  &::-webkit-scrollbar { display: none; }
}

.thumbnail-item {
  flex: 0 0 94px;
  height: 68px;
  padding: 0;
  overflow: hidden;
  border: 2px solid transparent;
  border-radius: 7px;
  background: #222;
  cursor: pointer;
  opacity: 0.78;
  transition: border-color 0.2s, opacity 0.2s;

  &.active { border-color: #ff8a20; opacity: 1; }
  img { display: block; width: 100%; height: 100%; object-fit: cover; }
}

.detail-content { position: relative; z-index: 2; }

.product-summary-card {
  position: relative;
  margin-top: -1px;
  overflow: hidden;
  border-radius: 18px 18px 0 0;
  background: #fff;
}

.product-title {
  padding: 19px 16px 5px;
  color: #24272c;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.4;
}

.product-subtitle { padding: 0 16px 8px; color: #73777f; font-size: 13px; }

.price-number-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  padding-left: 16px;
}

.product-price {
  display: flex;
  align-items: baseline;
  color: #ef4d0b;

  .currency { font-size: 17px; font-weight: 700; }
  strong { margin: 0 2px; font-size: 26px; line-height: 1; }
  .price-suffix { font-size: 13px; }
  &--empty { color: #8b8b8b; font-size: 15px; }
}

.product-number {
  min-width: 92px;
  padding: 7px 12px 7px 17px;
  border-radius: 18px 0 0 18px;
  color: #666;
  background: #f7f7f7;
  font-size: 12px;
  white-space: nowrap;
}

.keyword-list { display: flex; flex-wrap: wrap; gap: 6px; padding: 1px 16px 13px; }
.keyword-tag { padding: 3px 7px; border-radius: 3px; color: #ff720d; background: #fff0e3; font-size: 12px; line-height: 1.25; }

.purchase-hint {
  padding: 11px 16px;
  color: #666;
  background: #f8f8fc;
  font-size: 12px;
  line-height: 1.6;
}

.sku-section { display: flex; flex-direction: column; gap: 11px; padding: 12px 10px; }
.sku-card { padding: 15px; border-radius: 8px; background: #fff; box-shadow: 0 1px 2px rgba(31, 41, 55, 0.03); }
.sku-heading-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.sku-heading-copy { min-width: 0; flex: 1; }
.sku-title { color: #252525; font-size: 17px; font-weight: 700; line-height: 1.45; }
.sku-subtitle { margin-top: 3px; overflow: hidden; color: #8a8a8a; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }

.sku-price-pill {
  flex: 0 0 auto;
  padding: 5px 9px;
  border-radius: 5px;
  color: #e06419;
  background: #fff8e8;
  white-space: nowrap;

  span { font-size: 12px; }
  strong { margin: 0 2px; font-size: 18px; }
  small { color: #999; font-size: 11px; font-weight: 400; }
  &--empty { color: #999; font-size: 12px; }
}

.sku-tag-action-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 12px; }
.sku-tag-list { display: flex; flex: 1; flex-wrap: wrap; gap: 6px; }

.sku-tag {
  padding: 3px 8px;
  border-radius: 12px;
  color: #f0802d;
  background: #fff4e9;
  font-size: 11px;

  &--type { color: #e96c10; background: #fff0df; }
}

.sku-action-only { display: flex; justify-content: flex-end; margin-top: 10px; }

.reserve-btn {
  flex: 0 0 auto;
  min-width: 66px;
  height: 32px;
  padding: 0 17px;
  border: 0;
  border-radius: 16px;
  color: #fff;
  background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%);
  box-shadow: 0 3px 8px rgba(255, 114, 13, 0.18);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.spec-list { margin-top: 13px; padding-top: 4px; border-top: 1px solid #f0f0f0; }
.spec-row {
  padding: 12px 0;
  cursor: pointer;
  transition: background-color .2s;
  & + & { border-top: 1px solid #f4f4f4; }
  &--sold-out { opacity: 0.55; cursor: not-allowed; }
  &--selected { margin: 0 -8px; padding: 12px 8px; border-radius: 8px; background: #fff0e3; }
}
.spec-tag-list { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.spec-tag { padding: 3px 9px; border-radius: 10px; color: #404348; background: #f4f5f7; font-size: 12px; }

.spec-meta {
  display: flex;
  align-items: baseline;
  gap: 12px;

  strong { color: #ef4d0b; font-size: 17px; }
  span { color: #999; font-size: 12px; }
  .stock-out { color: #b2b2b2; }
}

.spec-empty { padding: 15px 0 5px; color: #999; font-size: 12px; text-align: center; }
.spec-purchase-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}
.spec-quantity { display: flex; align-items: center; gap: 10px; color: #252525; font-size: 13px; }
.spec-purchase-label { color: #666; }
.spec-quantity button { width: 27px; height: 27px; padding: 0; border: 1px solid #ffc28e; border-radius: 50%; color: #ff720d; background: #fff8f1; font-size: 17px; line-height: 1; cursor: pointer; }
.spec-quantity strong { min-width: 18px; text-align: center; color: #333; }
.spec-buy-btn { min-width: 96px; height: 36px; border: 0; border-radius: 18px; color: #fff; background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%); box-shadow: 0 3px 8px rgba(255, 114, 13, .18); font-size: 14px; font-weight: 600; cursor: pointer; }
.sku-rich-texts { margin-top: 12px; padding: 12px; border-radius: 8px; background: #fafafa; }

.sku-rich-text-item {
  & + & { margin-top: 12px; padding-top: 12px; border-top: 1px solid #ececec; }
  h3 { margin-bottom: 7px; color: #333; font-size: 14px; }
}

.product-content-section { padding-bottom: 12px; background: #fff; }

.content-tabs {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  gap: 4px;
  padding: 0 10px;
  overflow-x: auto;
  border-bottom: 1px solid #efefef;
  background: #fff;
  scrollbar-width: none;

  &::-webkit-scrollbar { display: none; }

  button {
    position: relative;
    flex: 0 0 auto;
    padding: 13px 11px 11px;
    border: 0;
    color: #666;
    background: transparent;
    font-size: 14px;
    cursor: pointer;

    &.active { color: #222; font-weight: 600; }
    &.active::after { position: absolute; right: 12px; bottom: 0; left: 12px; height: 3px; border-radius: 2px; background: #ff720d; content: ''; }
  }
}

.content-card {
  margin: 12px 10px 0;
  padding: 14px 12px;
  scroll-margin-top: 48px;
  border-radius: 8px;
  background: #fff8f1;
}

.content-title { margin-bottom: 12px; color: #d96718; font-size: 16px; font-weight: 700; text-align: center; }

.rich-text-body {
  max-width: 100%;
  overflow: hidden;
  color: #555;
  font-size: 14px;
  line-height: 1.75;
  overflow-wrap: anywhere;

  ::v-deep img,
  ::v-deep video { display: block; max-width: 100% !important; height: auto !important; margin: 8px auto; border-radius: 6px; }
  ::v-deep table { max-width: 100% !important; }
  ::v-deep p { margin: 0 0 8px; }
}

.page-state {
  display: flex;
  min-height: 52vh;
  align-items: center;
  justify-content: center;
  color: #777;
  background: #fff;

  &--error {
    flex-direction: column;
    gap: 14px;

    button { padding: 8px 18px; border: 0; border-radius: 18px; color: #fff; background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%); cursor: pointer; }
  }
}

@media (min-width: 751px) {
  .product-detail-page { box-shadow: 0 0 24px rgba(0, 0, 0, 0.08); }
}

@media (max-width: 390px) {
  .thumbnail-item { flex-basis: 82px; height: 60px; }
  .sku-card { padding: 13px; }
}
</style>
